import { useEffect, useState } from "react";
import { useAuth, useSession } from "@clerk/clerk-react";

const WELCOME_CLAIM_SEEN_PREFIX = "welcomeClaimSeen:";
/** Max age of a Clerk session (ms) to treat as "just logged in" (covers OAuth redirects). */
const FRESH_SESSION_MS = 2 * 60 * 1000;

function welcomeSeenKey(sessionId) {
  return `${WELCOME_CLAIM_SEEN_PREFIX}${sessionId}`;
}

/**
 * Dev-only welcome credits popup for UI testing.
 * Enable with REACT_APP_FORCE_CLAIM_POPUP=true in development.
 *
 * Shows once per new Clerk session (login / signup), including after OAuth redirect.
 * Does not show on reload — session is marked seen in sessionStorage.
 */
export function useMockWelcomeCreditsPopup() {
  const forceClaimPopup =
    process.env.REACT_APP_ENV === "development" &&
    process.env.REACT_APP_FORCE_CLAIM_POPUP === "true";

  const { isLoaded, isSignedIn } = useAuth();
  const { session } = useSession();
  const [show, setShow] = useState(false);
  const [blocking, setBlocking] = useState(forceClaimPopup);

  useEffect(() => {
    if (!forceClaimPopup) {
      setBlocking(false);
      return;
    }
    if (!isLoaded || !isSignedIn) return;

    const sessionId = session?.id;
    if (!sessionId) return;

    const seenKey = welcomeSeenKey(sessionId);
    if (sessionStorage.getItem(seenKey) === "1") {
      setBlocking(false);
      return;
    }

    const createdAt = session.createdAt ?? session.lastActiveAt;
    if (!createdAt) {
      setBlocking(false);
      return;
    }

    const sessionAgeMs = Date.now() - new Date(createdAt).getTime();

    // Existing session restored on reload — mark seen, never show.
    if (sessionAgeMs > FRESH_SESSION_MS) {
      sessionStorage.setItem(seenKey, "1");
      setBlocking(false);
      return;
    }

    setBlocking(true);
    sessionStorage.setItem(seenKey, "pending");
    const timer = window.setTimeout(() => {
      sessionStorage.setItem(seenKey, "1");
      setShow(true);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [forceClaimPopup, isLoaded, isSignedIn, session?.id, session?.createdAt, session?.lastActiveAt]);

  const close = () => {
    setShow(false);
    setBlocking(false);
  };

  return {
    enabled: forceClaimPopup,
    show,
    close,
    blocksToolEntry: forceClaimPopup && (blocking || show),
  };
}
